import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import adminApi from '../../api/adminClient';
import './Admin.css';

const emptyForm = {
  title: '', company: '', location: '', type: '정규직', salary: '',
  deadline: '', tagsText: '', description: '', requirementsText: '',
  isNew: true, isUrgent: false,
};

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingJob, setEditingJob] = useState(null); // null = 목록 보기, {} = 신규, {...} = 수정
  const [filter, setFilter] = useState('전체'); // 전체 / 게시중 / 마감

  useEffect(() => { loadJobs(); }, []);

  async function loadJobs() {
    try {
      setLoading(true);
      const data = await adminApi.get('/jobs?includeInactive=true');
      setJobs(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleStatus(job) {
    const nextActive = !job.isActive;
    const confirmMsg = nextActive
      ? `"${job.title}" 공고를 다시 게시하시겠습니까?`
      : `"${job.title}" 공고를 마감 처리하시겠습니까? 홈페이지에서 더 이상 보이지 않습니다.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await adminApi.patch(`/jobs/${job.id}/status`, { isActive: nextActive });
      loadJobs();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleDelete(job) {
    if (!window.confirm(`"${job.title}" 공고를 완전히 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) return;
    try {
      await adminApi.delete(`/jobs/${job.id}`);
      loadJobs();
    } catch (err) {
      alert(err.message);
    }
  }

  const filteredJobs = jobs.filter(j => {
    if (filter === '게시중') return j.isActive;
    if (filter === '마감') return !j.isActive;
    return true;
  });

  if (editingJob !== null) {
    return (
      <AdminLayout>
        <JobForm
          initial={editingJob}
          onCancel={() => setEditingJob(null)}
          onSaved={() => { setEditingJob(null); loadJobs(); }}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-header">
        <div>
          <h1>채용공고 관리</h1>
          <p className="text-muted">등록된 공고를 확인하고 신규 등록·수정·마감 처리할 수 있습니다.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditingJob({})}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          신규 공고 등록
        </button>
      </div>

      <div className="admin-filter-row">
        {['전체', '게시중', '마감'].map(f => (
          <button
            key={f}
            className={`type-filter__btn ${filter === f ? 'type-filter__btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f} {f !== '전체' && `(${jobs.filter(j => f === '게시중' ? j.isActive : !j.isActive).length})`}
          </button>
        ))}
      </div>

      {loading && <div className="state-message">불러오는 중입니다...</div>}
      {!loading && error && <div className="state-message state-message--error">{error}</div>}

      {!loading && !error && (
        filteredJobs.length === 0 ? (
          <div className="state-message">조건에 맞는 공고가 없습니다.</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>상태</th>
                  <th>제목</th>
                  <th>고용형태</th>
                  <th>근무지</th>
                  <th>마감일</th>
                  <th>등록일</th>
                  <th style={{ width: 220 }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map(job => (
                  <tr key={job.id} className={!job.isActive ? 'admin-table__row--inactive' : ''}>
                    <td>
                      <span className={`badge ${job.isActive ? 'badge-green' : 'badge-red'}`}>
                        {job.isActive ? '게시중' : '마감'}
                      </span>
                    </td>
                    <td className="admin-table__title">
                      {job.title}
                      {job.isNew && <span className="badge badge-accent" style={{ marginLeft: 6 }}>NEW</span>}
                      {job.isUrgent && <span className="badge badge-red" style={{ marginLeft: 6 }}>긴급</span>}
                    </td>
                    <td>{job.type}</td>
                    <td>{job.location}</td>
                    <td>{job.deadline}</td>
                    <td>{String(job.created_at).slice(0, 10)}</td>
                    <td className="admin-table__actions">
                      <button className="admin-action-btn" onClick={() => setEditingJob(job)}>수정</button>
                      <button className="admin-action-btn" onClick={() => handleToggleStatus(job)}>
                        {job.isActive ? '마감처리' : '재게시'}
                      </button>
                      <button className="admin-action-btn admin-action-btn--danger" onClick={() => handleDelete(job)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </AdminLayout>
  );
}

/* ── 등록/수정 폼 ── */
function JobForm({ initial, onCancel, onSaved }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState(() => {
    if (!isEdit) return emptyForm;
    return {
      title: initial.title || '',
      company: initial.company || '',
      location: initial.location || '',
      type: initial.type || '정규직',
      salary: initial.salary || '',
      deadline: initial.deadline || '',
      tagsText: (initial.tags || []).join(', '),
      description: initial.description || '',
      requirementsText: (initial.requirements || []).join('\n'),
      isNew: !!initial.isNew,
      isUrgent: !!initial.isUrgent,
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.company || !form.location || !form.type) {
      setError('제목, 회사명, 근무지, 고용형태는 필수입니다.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      title: form.title,
      company: form.company,
      location: form.location,
      type: form.type,
      salary: form.salary,
      deadline: form.deadline || null,
      tags: form.tagsText.split(',').map(s => s.trim()).filter(Boolean),
      description: form.description,
      requirements: form.requirementsText.split('\n').map(s => s.trim()).filter(Boolean),
      isNew: form.isNew,
      isUrgent: form.isUrgent,
    };

    try {
      if (isEdit) {
        await adminApi.put(`/jobs/${initial.id}`, { ...payload, isActive: initial.isActive });
      } else {
        await adminApi.post('/jobs', payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button className="back-btn" onClick={onCancel}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        목록으로
      </button>

      <div className="admin-form-card">
        <h2>{isEdit ? '채용공고 수정' : '신규 채용공고 등록'}</h2>

        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}

          <div className="form-row-2">
            <div className="form-group">
              <label>공고 제목 <span className="required">*</span></label>
              <input name="title" value={form.title} onChange={handleChange} placeholder="예: IT 개발자 (Java/Spring)" />
            </div>
            <div className="form-group">
              <label>고용 기업 <span className="required">*</span></label>
              <input name="company" value={form.company} onChange={handleChange} placeholder="예: 대기업 계열사 (비공개)" />
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>근무지 <span className="required">*</span></label>
              <input name="location" value={form.location} onChange={handleChange} placeholder="예: 서울 강남구" />
            </div>
            <div className="form-group">
              <label>고용형태 <span className="required">*</span></label>
              <select name="type" value={form.type} onChange={handleChange}>
                <option>정규직</option>
                <option>아르바이트</option>
                <option>도급직</option>
                <option>계약직</option>
                <option>파견직</option>
              </select>
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>급여</label>
              <input name="salary" value={form.salary} onChange={handleChange} placeholder="예: 연 4,000만원 ~ 5,500만원" autoComplete="off" />
            </div>
            <div className="form-group">
              <label>지원 마감일</label>
              <input type="date" name="deadline" value={form.deadline} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label>태그 <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>(쉼표로 구분)</span></label>
            <input name="tagsText" value={form.tagsText} onChange={handleChange} placeholder="예: Java, Spring, 경력 3년+" />
          </div>

          <div className="form-group">
            <label>업무 내용</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={4} placeholder="담당 업무를 설명해 주세요." />
          </div>

          <div className="form-group">
            <label>지원 자격 <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>(한 줄에 하나씩 입력)</span></label>
            <textarea name="requirementsText" value={form.requirementsText} onChange={handleChange} rows={4} placeholder={'예)\n경력 3년 이상\n관계형 DB 활용 능력'} />
          </div>

          <div className="admin-checkbox-row">
            <label className="checkbox-row">
              <input type="checkbox" name="isNew" checked={form.isNew} onChange={handleChange} style={{ width: 'auto' }} />
              <span>신규 공고로 표시 (NEW 배지)</span>
            </label>
            <label className="checkbox-row">
              <input type="checkbox" name="isUrgent" checked={form.isUrgent} onChange={handleChange} style={{ width: 'auto' }} />
              <span>긴급 채용으로 표시 (긴급 배지)</span>
            </label>
          </div>

          <div className="admin-form-actions">
            <button type="button" className="btn btn-outline-navy" onClick={onCancel}>취소</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '저장 중...' : isEdit ? '수정 완료' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
